import { getData, postData, putData, patchData, deleteData } from './FetchNodeServices.js';
import type { MockSalesOrder } from '@/mocks/billing';

export interface SalesOrderPayload {
  soNumber?: string;
  customerId: string;
  customerName: string;
  customerGSTIN?: string;
  quotationId?: string;
  proformaId?: string;
  warehouseId?: string;
  date?: string;
  expectedDeliveryDate?: string;
  status?: string;
  notes?: string;
  paymentTerms?: string;
  totalAmount: number;
  deliveredAmount?: number;
  pendingAmount?: number;
  items: Array<{
    itemId: string;
    itemName: string;
    hsnCode?: string;
    orderedQty: number;
    deliveredQty?: number;
    unit?: string;
    unitId?: string;
    rate: number;
    discount?: number;
    taxRate?: number;
    taxableAmount?: number;
    totalAmount?: number;
  }>;
}

export const salesOrderService = {
  listSalesOrders: async (params?: {
    search?: string;
    status?: string;
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<MockSalesOrder[]> => {
    try {
      const res = await getData('api/v1/sales-orders', params || {});
      if (res && res.success && Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      throw error;
    }
  },

  getSalesOrder: async (id: string): Promise<MockSalesOrder> => {
    try {
      const res = await getData(`api/v1/sales-orders/${id}`);
      if (res && res.success && res.data) {
        return res.data;
      }
      throw new Error(res?.message || 'Failed to fetch sales order');
    } catch (error) {
      console.error('Error fetching sales order detail:', error);
      throw error;
    }
  },

  createSalesOrder: async (payload: SalesOrderPayload): Promise<{ id: string; soNumber: string }> => {
    try {
      const res = await postData('api/v1/sales-orders', payload);
      if (res && res.success && res.data) {
        return res.data;
      }
      throw new Error(res?.message || 'Failed to create sales order');
    } catch (error) {
      console.error('Error creating sales order:', error);
      throw error;
    }
  },

  updateSalesOrder: async (id: string, payload: Partial<SalesOrderPayload>): Promise<{ id: string; soNumber: string }> => {
    try {
      const res = await putData(`api/v1/sales-orders/${id}`, payload);
      if (res && res.success && res.data) {
        return res.data;
      }
      throw new Error(res?.message || 'Failed to update sales order');
    } catch (error) {
      console.error('Error updating sales order:', error);
      throw error;
    }
  },

  updateStatus: async (
    id: string,
    status: string,
    extra?: { deliveredAmount?: number; pendingAmount?: number }
  ): Promise<boolean> => {
    try {
      const res = await patchData(`api/v1/sales-orders/${id}/status`, { status, ...extra });
      return !!(res && res.success);
    } catch (error) {
      console.error('Error updating sales order status:', error);
      throw error;
    }
  },

  deleteSalesOrder: async (id: string): Promise<boolean> => {
    try {
      const res = await deleteData(`api/v1/sales-orders/${id}`);
      return !!(res && res.success);
    } catch (error) {
      console.error('Error deleting sales order:', error);
      throw error;
    }
  },
};
